import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/Header';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Flex, Text, Button, Input, VStack, HStack,
    FormControl, FormLabel, Divider, Spinner, Center, Badge,
    Tabs, TabList, Tab, TabPanels, TabPanel, Icon,
} from '@chakra-ui/react';
import { LuArrowLeft } from 'react-icons/lu';
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor';
import TypeChipGroup from './TypeChipGroup';
import { getPlatform, updatePlatform, updatePlatformDocs } from './SdkConfigApi';
import {
    normalizePlatform, toDocsPayload, docKey, getDocType, DEFAULT_DOC_TYPE_ID,
} from './sdkDocsModel';

const PlatformEditor = () => {
    const { platformId } = useParams();
    const navigate = useNavigate();

    const [platform, setPlatform] = useState(null);
    const [loading, setLoading] = useState(true);
    const [metaSaving, setMetaSaving] = useState(false);
    const [docsSaving, setDocsSaving] = useState(false);

    const [label, setLabel] = useState('');
    const [heading, setHeading] = useState('');
    const [description, setDescription] = useState('');
    const [version, setVersion] = useState('');
    const [authToken, setAuthToken] = useState('');
    const [tagColor, setTagColor] = useState('');
    const [typeId, setTypeId] = useState(DEFAULT_DOC_TYPE_ID);
    const [docs, setDocs] = useState({});

    const loadPlatform = useCallback(async () => {
        setLoading(true);
        try {
            const p = normalizePlatform(await getPlatform(platformId));
            if (p) {
                setPlatform(p);
                setLabel(p.label);
                setHeading(p.heading || '');
                setDescription(p.description || '');
                setVersion(p.version);
                setAuthToken(p.auth_token || '');
                setTagColor(p.tag_color);
                setTypeId(p.type_id);
                setDocs(p.docs);
            }
        } catch (error) {
            console.error('Error loading platform:', error);
            toast.error('Failed to load platform');
        } finally {
            setLoading(false);
        }
    }, [platformId]);

    useEffect(() => {
        loadPlatform();
    }, [loadPlatform]);

    const saveMeta = async () => {
        setMetaSaving(true);
        try {
            const meta = {
                label, heading, description, version,
                auth_token: authToken, tag_color: tagColor, type_id: typeId,
            };
            await updatePlatform(platformId, meta);
            setPlatform(prev => ({ ...prev, ...meta }));
            toast.success('Platform details saved');
        } catch (error) {
            console.error('Error saving platform details:', error);
            toast.error('Failed to save platform details');
        } finally {
            setMetaSaving(false);
        }
    };

    // One Save covers every variant tab. Each RichTextEditor writes into the flat
    // `docs` map regardless of which tab is open, so saving has to persist all of
    // them together rather than only whichever tab happens to be active.
    const saveDocs = async () => {
        setDocsSaving(true);
        try {
            await updatePlatformDocs(platformId, typeId, toDocsPayload(docs));
            setPlatform(prev => ({ ...prev, docs }));
            toast.success('Documentation saved');
        } catch (error) {
            console.error('Error saving documentation:', error);
            toast.error('Failed to save documentation');
        } finally {
            setDocsSaving(false);
        }
    };

    const setDoc = (variantId, value) =>
        setDocs(prev => ({ ...prev, [docKey(variantId)]: value }));

    const getDoc = (variantId) => docs[docKey(variantId)] || '';

    if (loading) {
        return (
            <>
                <Header />
                <Center h="400px">
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                </Center>
            </>
        );
    }

    if (platform) {
        const hasVariants = (platform.variants || []).length > 0;
        const color = platform.tag_color || '#718096';
        const docType = getDocType(typeId);
        const variantLabels = (platform.variants || []).map(v => v.label).join(' / ');

        return (
            <>
                <Header />
                <Box p={8} m={8}>
                    <HStack mb={6} spacing={3}>
                        <Button
                            size="sm"
                            leftIcon={<Icon as={LuArrowLeft} />}
                            variant="ghost"
                            onClick={() => navigate('/sdk-config')}
                        >
                            SDK Config
                        </Button>
                        <Text color="gray.300">/</Text>
                        <Box w="10px" h="10px" borderRadius="full" bg={color} />
                        <Text fontSize="xl" fontWeight="bold" color="gray.700">
                            {platform.label}
                        </Text>
                    </HStack>

                    {/* Metadata */}
                    <Box p={5} mb={6} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                        <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={4}>
                            Platform Details
                        </Text>
                        <VStack align="stretch" spacing={4}>
                            <Flex gap={4} align="flex-end" flexWrap="wrap">
                                <FormControl flex="1" minW="130px">
                                    <FormLabel fontSize="xs" color="gray.500">Label</FormLabel>
                                    <Input size="sm" value={label} onChange={e => setLabel(e.target.value)} />
                                </FormControl>

                                <FormControl flex="1" minW="120px">
                                    <FormLabel fontSize="xs" color="gray.500">Min Version</FormLabel>
                                    <Input
                                        size="sm"
                                        value={version}
                                        onChange={e => setVersion(e.target.value)}
                                        placeholder="e.g. 24.0"
                                    />
                                </FormControl>

                                <FormControl flex="2" minW="180px">
                                    <FormLabel fontSize="xs" color="gray.500">Auth Token</FormLabel>
                                    <Input
                                        size="sm"
                                        value={authToken}
                                        onChange={e => setAuthToken(e.target.value)}
                                        placeholder="jp_xxx..."
                                        fontFamily="mono"
                                        fontSize="xs"
                                    />
                                </FormControl>

                                <FormControl flex="0 0 auto">
                                    <FormLabel fontSize="xs" color="gray.500">Tag Color</FormLabel>
                                    <HStack spacing={2}>
                                        <Input
                                            type="color"
                                            size="sm"
                                            value={tagColor}
                                            onChange={e => setTagColor(e.target.value)}
                                            w="40px"
                                            p={1}
                                            cursor="pointer"
                                        />
                                        <Input
                                            size="sm"
                                            value={tagColor}
                                            onChange={e => setTagColor(e.target.value)}
                                            fontFamily="mono"
                                            fontSize="xs"
                                            w="100px"
                                        />
                                    </HStack>
                                </FormControl>
                            </Flex>

                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500">Main Heading</FormLabel>
                                <Input
                                    size="sm"
                                    value={heading}
                                    onChange={e => setHeading(e.target.value)}
                                    placeholder="e.g. Kotlin SDK Integration"
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500">Documentation Type</FormLabel>
                                <TypeChipGroup
                                    value={typeId}
                                    onChange={setTypeId}
                                    isDisabled={metaSaving}
                                />
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="xs" color="gray.500">Description</FormLabel>
                                <RichTextEditor
                                    value={description}
                                    onChange={setDescription}
                                    placeholder="e.g. This document provides step-by-step instructions to integrate and initialize the SDK in your Android application."
                                />
                            </FormControl>

                            <Button
                                size="sm"
                                colorScheme="blue"
                                onClick={saveMeta}
                                isLoading={metaSaving}
                                alignSelf="flex-start"
                            >
                                Save
                            </Button>
                        </VStack>
                    </Box>

                    {/* Documentation — one type per platform, split by OS variant */}
                    <Box p={5} border="1px solid" borderColor="gray.200" borderRadius="md" bg="white">
                        <HStack justify="space-between" mb={4}>
                            <HStack spacing={3}>
                                <Text fontSize="sm" fontWeight="semibold" color="gray.600">
                                    Documentation
                                </Text>
                                <Badge colorScheme={docType.colorScheme} variant="solid" fontSize="10px">
                                    {docType.label}
                                </Badge>
                                {hasVariants && (
                                    <Badge colorScheme="gray" variant="subtle" fontSize="10px">
                                        {variantLabels}
                                    </Badge>
                                )}
                            </HStack>
                            <Button
                                size="xs"
                                colorScheme="blue"
                                onClick={saveDocs}
                                isLoading={docsSaving}
                            >
                                Save
                            </Button>
                        </HStack>

                        {hasVariants ? (
                            <>
                                <VStack align="stretch" spacing={3}>
                                    <Text fontSize="xs" color="gray.500">
                                        Shared — shown above the {variantLabels} tabs
                                    </Text>
                                    <RichTextEditor
                                        value={getDoc(null)}
                                        onChange={val => setDoc(null, val)}
                                        placeholder={`Intro for ${docType.label} that applies to every OS (optional)...`}
                                    />
                                </VStack>

                                <Divider my={6} />

                                {/* OS variant tabs */}
                                <Tabs variant="unstyled" size="sm" isLazy>
                                    <TabList
                                        bg="#f1f5f9"
                                        p="1"
                                        borderRadius="full"
                                        display="inline-flex"
                                        mb={4}
                                    >
                                        {platform.variants.map(v => (
                                            <Tab
                                                key={v.id}
                                                _selected={{ bg: 'white', boxShadow: 'sm', fontWeight: '600' }}
                                                borderRadius="full"
                                                fontSize="sm"
                                            >
                                                {v.label}
                                            </Tab>
                                        ))}
                                    </TabList>

                                    <TabPanels>
                                        {platform.variants.map(v => (
                                            <TabPanel key={v.id} px={0} pt={0}>
                                                <RichTextEditor
                                                    value={getDoc(v.id)}
                                                    onChange={val => setDoc(v.id, val)}
                                                    placeholder={`${v.label} instructions for ${docType.label}...`}
                                                />
                                            </TabPanel>
                                        ))}
                                    </TabPanels>
                                </Tabs>
                            </>
                        ) : (
                            <RichTextEditor
                                value={getDoc(null)}
                                onChange={val => setDoc(null, val)}
                                placeholder={`${docType.label} instructions...`}
                            />
                        )}
                    </Box>
                </Box>
            </>
        );
    }

    return (
        <>
            <Header />
            <Center h="400px">
                <Text color="gray.500">Platform not found.</Text>
            </Center>
        </>
    );
};

export default PlatformEditor;
